import Box from '@mui/material/Box'
import { urlFromLocationName } from '../../_Components/addUrlToLocation.js'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTranslation } from 'react-i18next'

const WarningRandomCheck = ({ checkpointInfo }) => {
  const theme = useTheme()

  if (!checkpointInfo || checkpointInfo.length === 0) return null

  return (
    <Box sx={{ my: 3, p: 2, borderRadius: 2, borderLeft: `2px solid ${theme.palette.warning.main}` }}>
      <Typography variant='h6' gutterBottom sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
        ⚠️ {checkpointInfo[0].title}
      </Typography>

      {checkpointInfo[0].sections.map((section, index) => (
        <Box key={index} sx={{ mb: index < checkpointInfo[0].sections.length - 1 ? 2 : 0 }}>
          <Typography variant='subtitle2' sx={{ fontWeight: 'bold', color: theme.palette.warning.main, mb: 0.5 }}>
            {section.subtitle}
          </Typography>
          <Typography variant='body2' sx={{ color: theme.palette.text.secondary, whiteSpace: 'pre-line' }}>
            {section.text}
          </Typography>
        </Box>
      ))}
    </Box>
  )
}

export default function CheckpointsGuide ({ race, distance }) {
  const { t } = useTranslation('raceGuide')
  const distanceData = race?.raceDistances?.find(r => r.distance === distance) || {}
  const { checkpoints } = distanceData
  const { locations } = race || {}
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  if (!checkpoints) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant='body1'>{t('checkpoints.noData')}</Typography>
      </Box>
    )
  }

  // // Convert checkpoints object to array and sort by checkpoint number
  // const checkpointsArray = Object.entries(checkpoints)
  //   .map(([key, value]) => ({ ...value, key }))
  //   .sort((a, b) => {
  //     // Sort by checkpoint number (extract number from key for numbered checkpoints)
  //     const aNum = a.key === 'start' ? 0 : a.key === 'finish' ? 999 : parseInt(a.key) || 0
  //     const bNum = b.key === 'start' ? 0 : b.key === 'finish' ? 999 : parseInt(b.key) || 0
  //     return aNum - bNum
  //   })

  const info = t('checkpoints.info')

  // Mobile view - stacked cards
  if (isMobile) {
    return (
      <Box sx={{ width: '100%', overflowX: 'auto' }}>
        <Typography
          variant='body2' sx={{
          my: 1
        }}
        >{info}</Typography>

        {distanceData?.checkpointInfo && <WarningRandomCheck {...{ checkpointInfo: distanceData?.checkpointInfo }} />}

        {checkpoints.map((checkpoint, i) => (
          <Paper key={i} elevation={3} sx={{ mb: 2, p: 2, borderLeft: `4px solid ${theme.palette.primary.main}` }}>
            <Typography variant='subtitle1' gutterBottom sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
              {checkpoint.cp === 'START' ? t('checkpoints.start') : checkpoint.cp === 'FINISH' ? t('checkpoints.finish') : t('checkpoints.checkpoint', { cp: checkpoint.cp })}
            </Typography>

            <Typography variant='body2' sx={{ mb: 1 }}>
              <strong>{t('checkpoints.name')}</strong> {urlFromLocationName(locations, checkpoint.name) || checkpoint.name}
            </Typography>

            {checkpoint.location && (
              <Typography variant='body2' sx={{ mb: 1 }}>
                <strong>{t('checkpoints.location')}</strong> {urlFromLocationName(locations, checkpoint.location) || checkpoint.location}
              </Typography>
            )}

            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1, mb: 1 }}>
              {checkpoint.interDistKm && (
                <Typography variant='body2'>
                  <strong>{t('checkpoints.interDist')}</strong> {t('checkpoints.kmValue', { value: checkpoint.interDistKm })}
                </Typography>
              )}
              {checkpoint.commDistKm && (
                <Typography variant='body2'>
                  <strong>{t('checkpoints.commDist')}</strong> {t('checkpoints.kmValue', { value: checkpoint.commDistKm })}
                </Typography>
              )}
              {checkpoint.commDPlusM && (
                <Typography variant='body2'>
                  <strong>{t('checkpoints.commDPlus')}</strong> {t('checkpoints.mValue', { value: checkpoint.commDPlusM })}
                </Typography>
              )}
              {checkpoint.commDMinusM && (
                <Typography variant='body2'>
                  <strong>{t('checkpoints.commDMinus')}</strong> {t('checkpoints.mValue', { value: checkpoint.commDMinusM })}
                </Typography>
              )}
              {checkpoint.altitudeM && (
                <Typography variant='body2'>
                  <strong>{t('checkpoints.altitude')}</strong> {t('checkpoints.mValue', { value: checkpoint.altitudeM })}
                </Typography>
              )}
            </Box>

            {checkpoint.crewSupport && (
              <Typography variant='body2' sx={{ mb: 1 }}>
                <strong>{t('checkpoints.crewSupport')}</strong> {checkpoint.crewSupport}
              </Typography>
            )}

            {checkpoint.services && (
              <Typography variant='body2' sx={{ mb: 1 }}>
                <strong>{t('checkpoints.services')}</strong> {checkpoint.services}
              </Typography>
            )}

            {checkpoint.cutOffTime && (
              <Typography variant='body2' sx={{ color: theme.palette.error.main, fontWeight: 'medium' }}>
                <strong>{t('checkpoints.cutOff')}</strong> {checkpoint.cutOffTime}
              </Typography>
            )}
          </Paper>
        ))}
      </Box>
    )
  }

  // Desktop view - table
  return (
    <Box sx={{ width: '100%', overflowX: 'auto' }}>
      <Typography
        variant='body2' sx={{
        my: 1
      }}
      >{info}</Typography>

      {distanceData?.checkpointInfo && <WarningRandomCheck {...{ checkpointInfo: distanceData?.checkpointInfo }} />}
      <TableContainer component={Paper}>
        <Table aria-label={t('checkpoints.tableAria')} size='small'>
          <TableHead sx={{ backgroundColor: '#222' }}>
            <TableRow>
              <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.75rem', textAlign: 'center' }}></TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.75rem', textAlign: 'center' }}>{t('checkpoints.headers.cpName')}</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.75rem', textAlign: 'center' }}>{t('checkpoints.headers.interDistKm')}</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.75rem', textAlign: 'center' }}>{t('checkpoints.headers.commDistKm')}</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.75rem', textAlign: 'center' }}>{t('checkpoints.headers.commDPlusM')}</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.75rem', textAlign: 'center' }}>{t('checkpoints.headers.commDMinusM')}</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.75rem', textAlign: 'center' }}>{t('checkpoints.headers.altitudeM')}</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.75rem', textAlign: 'center' }}>{t('checkpoints.headers.crewSupport')}</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.75rem', textAlign: 'center' }}>{t('checkpoints.headers.services')}</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.75rem', textAlign: 'center' }}>{t('checkpoints.headers.location')}</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.75rem', textAlign: 'center' }}>{t('checkpoints.headers.cutOff')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {checkpoints.map((checkpoint, j) => (
              <TableRow key={j} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                <TableCell component='th' scope='row' sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>
                  {checkpoint.cp === 'START' ? t('checkpoints.start') : checkpoint.cp === 'FINISH' ? t('checkpoints.finish') : checkpoint.cp}
                </TableCell>
                <TableCell sx={{ fontSize: '0.75rem' }}>
                  {urlFromLocationName(locations, checkpoint.name) || checkpoint.name}
                </TableCell>
                <TableCell>{checkpoint.interDistKm || '-'}</TableCell>
                <TableCell>{checkpoint.commDistKm || '-'}</TableCell>
                <TableCell>{checkpoint.commDPlusM || '-'}</TableCell>
                <TableCell>{checkpoint.commDMinusM || '-'}</TableCell>
                <TableCell>{checkpoint.altitudeM && checkpoint.altitudeM !== 'NO' ? checkpoint.altitudeM : '-'}</TableCell>
                <TableCell>{checkpoint.crewSupport || '-'}</TableCell>
                <TableCell>{checkpoint.services || '-'}</TableCell>
                <TableCell>
                  {checkpoint.location ? urlFromLocationName(locations, checkpoint.location) || checkpoint.location : '-'}
                </TableCell>
                <TableCell sx={{ fontWeight: 'medium', color: checkpoint.cutOffTime ? theme.palette.error.main : 'inherit' }}>
                  {checkpoint.cutOffTime || '-'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}
